(ns nosco.richtext.editor-skeleton
  "A lightweight skeleton/placeholder for the HTMLEditor that can be used as a
   Suspense fallback. Does NOT import ProseMirror or other heavy editor deps.

   Renders the same DOM structure and CSS classes as the real editor so there is
   no layout shift when the editor chunk loads."
  (:require
   [clojure.string :as string]
   [hx.react :refer [defnc]]
   [nosco.i18n.translate :as t]
   [nosco.icons2 :as icons2]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.html :as html]
   [nosco.ui.pills :as pills]))

;; Mapping from toolbar layout keywords to the icons that appear as top-level
;; buttons in the toolbar. Popups (like :text-size) show a single trigger icon.
;; A string entry stands for a text pill rather than an icon button.
;; This must be kept in sync with nosco.richtext.toolbar/HTMLEditorToolbar.
;;
;; TODO: CommentHTMLEditor (nosco.ui.comments) uses a dual toolbar layout
;; (top + bottom) that is not yet supported by EditorSkeleton. Add support for
;; :toolbar-layout-bottom in a second pass.
(defn- toolbar-layout->icons []
  {:text-size          [icons2/TextSize-small]
   :text-size--document [icons2/TextSize-small]
   :text-style         [icons2/TextStyleBold-small
                        icons2/TextStyleItalic-small
                        icons2/TextStyleUnderline-small]
   :text-style-combo   [icons2/TextStyleBold-small]
   :paragraph-style    [icons2/AlignLeft-small icons2/ListBullets-small]
   :links              [icons2/Link-small]
   :upload-combo       [icons2/Add-small]
   :images             [icons2/Image-small]
   :files              [icons2/Attach-small]
   :videos             [icons2/Video-small]
   :ai                 [icons2/AI-small]
   :merge-tags         [] ;; merge-tags uses a custom pill component, skip in skeleton
   :formula-refs       [(t/t "#Fields")]
   :formula-functions  [(t/t "Functions")]
   :formula-operators  [(t/t "Operators")]})

(defn toolbar-layout->icon-groups
  "Given a toolbar layout vector (e.g. [:text-size :text-style :links]),
   returns a vector of icon-groups for rendering a skeleton toolbar.
   Each icon-group is a vector of icon keywords."
  [layout]
  (into []
        (keep (fn [group-key]
                (when-let [icons (get (toolbar-layout->icons) group-key)]
                  (when (seq icons)
                    icons))))
        layout))

(defnc EditorSkeleton
  "A skeleton placeholder that matches the HTMLEditor's layout to prevent
   layout jumps while the editor chunk is loading via Suspense.
   Shows the initial HTML content dimmed with disabled toolbar buttons and progress cursor.
   Uses the same CSS classes as the real ProseMirror editor for exact padding/font match.

   Props:
   - :toolbar-layout - vector of toolbar layout keywords (same as HTMLEditor's :toolbar-layout)
   - :no-border - match the editor's no-border variant
   - :initial-html - HTML string to show as dimmed content
   - :content-class - extra CSS class(es) for the content div
   - :content-style - inline style map for the content div"
  [{:keys [toolbar-layout no-border initial-html content-class content-style]
    :or {toolbar-layout [:text-size :text-style :paragraph-style
                         :links :images :files :videos]}}]
  (let [icon-groups (toolbar-layout->icon-groups toolbar-layout)]
    [:div {:class ["nosco-richtext-editor"
                   "nosco-richtext-editor--loading"
                   (when no-border "nosco-richtext-editor--no-border")]
           :style {:cursor "progress"
                   :pointer-events "none"
                   :user-select "none"}}
     (when (and (not no-border) (seq icon-groups))
       [:div {:class "nosco-richtext-menubar"}
        (for [[icons group-idx] (map vector icon-groups (range))]
          [:<> {:key group-idx}
           (for [[icon icon-idx] (map vector icons (range))]
             (if (string? icon)
               [pills/Pill {:key icon-idx
                            :border true
                            :size :condensed
                            :dropdown true
                            :label icon}]
               [buttons2/Button {:key icon-idx
                                 :variant :icon-gray
                                 :icon icon
                                 :disabled true}]))
           (when (< (inc group-idx) (count icon-groups))
             [:span {:class "nosco-toolbar-separator"}])])])
     [:div {:class ["ProseMirror" content-class]
            :style content-style}
      (when-not (string/blank? initial-html)
        [:div {:dangerouslySetInnerHTML
               #js {:__html (html/sanitize-html-editor (str initial-html))}}])]]))
